import { FilterOutlined } from "@ant-design/icons"
import { Badge, Button, Flex, Select } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { neutral, shadows } from "../../../../components/widgets/tokens.ts"
import {
  filterByLabelOrValue,
  mergeSelectedIntoOptions
} from "../../../DashboardPage/components/Portfolio/FilterBar.tsx"
import { countActiveReportFilters } from "../../helpers/reportWidgetFilters.ts"
import { useReportFilterOptions } from "../../hooks/useReportFilterOptions.ts"
import {
  REPORT_FILTER_DIMENSIONS,
  type ReportFilterDimension,
  type ReportWidgetFilters
} from "../../types/canvas.types.ts"

const DIMENSION_LABEL_KEYS: Record<ReportFilterDimension, string> = {
  project: "reports.filters.project",
  pm: "reports.filters.pm",
  client: "reports.filters.client",
  project_type: "reports.filters.projectType",
  project_status: "reports.filters.projectStatus",
  status: "reports.filters.status",
  state: "reports.filters.state"
}

interface ReportFilterBarProps {
  filters: ReportWidgetFilters
  onChange: (filters: ReportWidgetFilters) => void
  disabled?: boolean
}

export const ReportFilterBar = ({
  filters,
  onChange,
  disabled = false
}: ReportFilterBarProps) => {
  const { t } = useTranslation()
  const { options, loading } = useReportFilterOptions()
  const activeCount = countActiveReportFilters(filters)

  const handleDimensionChange =
    (dimension: ReportFilterDimension) => (value: string[]) => {
      onChange({ ...filters, [dimension]: value })
    }

  return (
    <Flex
      wrap="wrap"
      align="center"
      gap="small"
      style={{
        padding: "10px 12px",
        border: `1px solid ${neutral.border}`,
        borderRadius: 12,
        background: neutral.surface,
        boxShadow: shadows.card
      }}
    >
      <Flex
        align="center"
        gap={6}
        style={{ color: neutral.muted, fontWeight: 600 }}
      >
        <Badge count={activeCount} size="small" offset={[2, -2]}>
          <FilterOutlined />
        </Badge>
        <span>{t("reports.filters.title")}</span>
      </Flex>

      {REPORT_FILTER_DIMENSIONS.map((dimension) => (
        <Select<string[]>
          key={dimension}
          mode="multiple"
          allowClear
          showSearch
          aria-label={t(DIMENSION_LABEL_KEYS[dimension])}
          loading={loading}
          disabled={disabled}
          maxTagCount="responsive"
          filterOption={filterByLabelOrValue}
          placeholder={t(DIMENSION_LABEL_KEYS[dimension])}
          value={filters[dimension] ?? []}
          options={mergeSelectedIntoOptions(
            options[dimension],
            filters[dimension]
          )}
          onChange={handleDimensionChange(dimension)}
          style={{ minWidth: 168 }}
        />
      ))}

      <Button
        size="small"
        onClick={() => onChange({})}
        disabled={disabled || activeCount === 0}
      >
        {t("reports.filters.clear")}
      </Button>
    </Flex>
  )
}
